import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { loginPhoneCode } from '@/features/users/loginPhoneError';

import { useProvisionTenant, type ProvisionTenantBody } from './useProvisioning';

/**
 * + New tenant — canvas `1t` (slice 18b).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THIS BUTTON WAS FORBIDDEN BY A TEST UNTIL NOW, AND CORRECTLY SO.
 * ─────────────────────────────────────────────────────────────────────────────
 * `Tenants.test.tsx` has asserted since 17b that the words "new tenant" appear
 * nowhere on the screen, because there was nowhere to POST them: nothing in the
 * API inserted a tenant, and the pilot's was created by hand in SQL. Slice 18
 * shipped vehicle provisioning and that guard STAYED, deliberately — shipping
 * the neighbouring feature is not a reason to relax a guard the neighbouring
 * feature did not satisfy. It comes off now because the endpoint exists.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 TWO FIELDS DELIBERATELY REFUSE TO BE HELPFUL.
 * ─────────────────────────────────────────────────────────────────────────────
 * PLAN has NO pre-selected value. The column defaults to `personal`, and the
 * pilot reads `personal` with nobody able to say whether that was chosen. A
 * pre-filled select looks exactly like an answer, so this one starts empty and
 * the form cannot be submitted until staff pick.
 *
 * MONTHLY RATE starts BLANK and says so: blank means "no rate recorded", which
 * is a different statement from "$0.00". It must be possible to leave it empty
 * ON PURPOSE, so there is no placeholder zero and no auto-fill from the plan.
 */

type Plan = 'track' | 'fleet' | 'pro';

interface Draft {
  name: string;
  plan: Plan | '';
  /** Dollars as typed. Converted to integer cents exactly once, on submit. */
  rate: string;
  timezone: string;
  language: 'en' | 'so';
  ownerName: string;
  ownerPhone: string;
  ownerPassword: string;
}

const EMPTY: Draft = {
  name: '',
  plan: '', // 🔴 no default. See the header.
  rate: '',
  timezone: 'Africa/Mogadishu',
  language: 'en',
  ownerName: '',
  ownerPhone: '',
  ownerPassword: '',
};

const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary';

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  // The hint sits OUTSIDE the <label>: nested, it becomes part of the control's
  // accessible name, so the field is announced as its label plus a sentence and
  // cannot be found by its own name. Learned in slice 18.
  return (
    <div className="block">
      <label className="block">
        <span className="mb-1 block text-[12px] font-medium text-raad-muted">{label}</span>
        {children}
      </label>
      {hint ? <span className="mt-1 block text-[11.5px] text-raad-muted">{hint}</span> : null}
    </div>
  );
}

/**
 * Dollars as typed → integer cents, or null when blank.
 *
 * 🔴 `null` and `0` are DIFFERENT and both reachable: blank means no rate
 * recorded, a typed `0` means a deliberate free tenant. Money is integer cents
 * and this is the only place the conversion happens.
 */
export function toCents(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100);
}

export function NewTenantDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ tenantId: string } | null>(null);
  const provision = useProvisionTenant();

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const ready =
    draft.name.trim().length > 0 &&
    draft.plan !== '' &&
    draft.ownerName.trim().length > 0 &&
    draft.ownerPhone.trim().length >= 7 &&
    draft.ownerPassword.length >= 12;

  async function submit() {
    if (!ready) return;
    setError(null);
    const body: ProvisionTenantBody = {
      name: draft.name.trim(),
      plan: draft.plan as Plan,
      monthlyRateCents: toCents(draft.rate),
      timezone: draft.timezone.trim() || undefined,
      language: draft.language,
      owner: {
        phone: draft.ownerPhone.trim(),
        name: draft.ownerName.trim(),
        password: draft.ownerPassword,
      },
    };
    try {
      const res = await provision.mutateAsync(body);
      setDone({ tenantId: res.tenantId });
    } catch (e) {
      // SLICE 52 — the owner phone is now validated as a login: normalised to
      // E.164, and rejected if it is a tracker SIM or already belongs to another
      // user or a staff member. Those refusals get their own sentence; anything
      // else keeps the previous behaviour of surfacing the error's message.
      const code = loginPhoneCode(e);
      if (code !== null) setError(t(`editLogin.error.${code}`));
      else setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('newTenant.title')}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-2xl sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">{t('newTenant.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('newTenant.close')}
          </button>
        </div>

        {done ? (
          <div>
            <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12.5px]">
              {t('newTenant.created', { name: draft.name })}
            </p>
            {/* Said explicitly, because there is no SMS or email to send it: the
                password staff typed is the only way in, and nothing will
                deliver it for them. */}
            <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('newTenant.tellThemPassword', { phone: draft.ownerPhone })}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('newTenant.name')}>
                <input
                  className={INPUT}
                  value={draft.name}
                  onChange={(e) => set('name', e.target.value)}
                  autoFocus
                />
              </Field>

              <Field label={t('newTenant.plan')} hint={t('newTenant.planHint')}>
                <select
                  className={INPUT}
                  value={draft.plan}
                  onChange={(e) => set('plan', e.target.value as Plan | '')}
                >
                  {/* 🔴 An empty first option, and it is not decorative — see the
                      file header. Staff must choose. */}
                  <option value="">{t('newTenant.choosePlan')}</option>
                  <option value="track">{t('newTenant.planTier.track')}</option>
                  <option value="fleet">{t('newTenant.planTier.fleet')}</option>
                  <option value="pro">{t('newTenant.planTier.pro')}</option>
                </select>
              </Field>

              <Field label={t('newTenant.rate')} hint={t('newTenant.rateHint')}>
                <input
                  className={INPUT}
                  value={draft.rate}
                  onChange={(e) => set('rate', e.target.value)}
                  inputMode="decimal"
                  // No placeholder "0". Blank must read as blank.
                  placeholder=""
                />
              </Field>

              <Field label={t('newTenant.timezone')}>
                <input
                  className={INPUT}
                  value={draft.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                />
              </Field>

              <Field label={t('newTenant.language')}>
                <select
                  className={INPUT}
                  value={draft.language}
                  onChange={(e) => set('language', e.target.value as 'en' | 'so')}
                >
                  <option value="en">English</option>
                  <option value="so">Soomaali</option>
                </select>
              </Field>
            </div>

            <h3 className="mt-5 mb-3 text-[13px] font-semibold">{t('newTenant.ownerHeading')}</h3>
            <p className="mb-3 text-[12px] text-raad-muted">{t('newTenant.ownerWhy')}</p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('newTenant.ownerName')}>
                <input
                  className={INPUT}
                  value={draft.ownerName}
                  onChange={(e) => set('ownerName', e.target.value)}
                />
              </Field>
              <Field label={t('newTenant.ownerPhone')} hint={t('newTenant.ownerPhoneHint')}>
                <input
                  className={INPUT}
                  value={draft.ownerPhone}
                  onChange={(e) => set('ownerPhone', e.target.value)}
                  inputMode="tel"
                  placeholder="+2526…"
                />
              </Field>
              <Field label={t('newTenant.ownerPassword')} hint={t('newTenant.ownerPasswordHint')}>
                <input
                  className={INPUT}
                  type="password"
                  value={draft.ownerPassword}
                  onChange={(e) => set('ownerPassword', e.target.value)}
                />
              </Field>
            </div>

            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={submit}
                disabled={!ready || provision.isPending}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {provision.isPending ? t('newTenant.creating') : t('newTenant.create')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
